<template>
  <div>
    <px-view-header title="Alçadas de solicitação" title-class="font-weight-bold"
                    :breadcrumbs="breadcrumbs"/>
    <div v-if="loading">
      <px-circular-loading/>
    </div>
    <div class="mt-5" v-else>
      <px-alcadas-list :alcadas="alcada"
                       :opcoes-criterios="opcoesCriterios"
                       :criterios-enum="criteriosEnum"
                       :loading-save="loadingSave"
                       :loading-remove="loadingRemove"
                       :roles-aprovador="['ROLE_APROVADOR_REQUISICAO']"
                       :readonly="!hasRoleConfiguradorAlcadaRequisicao"
                       @save="onClickSave"
                       @remove="onClickRemove"/>
    </div>
    <px-confirm-dialog ref="confirm"/>
  </div>
</template>

<script>
import AlcadasList from '@/modules/alcadas/components/AlcadasList.vue';
import {
  CriteriosAprovacaoCompra,
  CriteriosAprovacaoSolicitacao,
} from 'px-business-components';
import AlcadasRequisicaoService from '@/modules/alcadas/alcadas-requisicao-service';
import { mapGetters } from 'vuex';
import ErpCampoService from '@/api/erp-campo-service';
import alcadasCadastrosAdicionaisConstants
  from '@/modules/alcadas/alcadas-cadastros-adicionais-constants';
import { mapCadastroAdicionalAlcadaByErp } from './components/alcadas-erp-utils';

export default {
  data() {
    return {
      loading: false,
      loadingSave: false,
      loadingRemove: false,
      alcada: null,
      criteriosEnum: [],
      opcoesCriterios: [],
      breadcrumbs: [
        { text: 'Configurações' },
        { text: 'Alçadas de solicitação' },
      ],
    };
  },
  computed: {
    ...mapGetters('user', ['hasRoleConfiguradorAlcadaRequisicao']),
    ...mapGetters('entidade', ['erpCamposIntegracao']),
  },
  methods: {
    getAlcadas() {
      this.loading = true;
      AlcadasRequisicaoService.get()
        .then(({ data }) => {
          this.alcada = data ? this.buildAlcadaOnLoad(data) : null;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    buildAlcadaOnLoad(alcada) {
      return {
        ...alcada,
        regras: alcada.regras.map(regra => ({
          ...regra,
          $$id: regra.id,
          criterios: regra.criterios.map((criterio) => {
            if (criterio.campo === CriteriosAprovacaoCompra.keys.CADASTRO_ADICIONAL.key) {
              return {
                campo: criterio.values[0]?.tipo,
                excecao: criterio.excecao,
                values: criterio.values,
                $$id: criterio.id,
              };
            }
            return {
              ...criterio,
              values: criterio.values,
              $$id: criterio.id,
            };
          }),
        })),
      };
    },
    buildCriterios(criterio) {
      const opcaoCriterio = this.opcoesCriterios.find(c => c.key === criterio.campo);
      return {
        $$id: undefined,
        excecao: criterio.excecao || false,
        id: criterio.id ?? undefined,
        campo: opcaoCriterio?.isCampoAdicional
          ? CriteriosAprovacaoCompra.keys.CADASTRO_ADICIONAL.key : criterio.campo,
        values: criterio.values.map((value) => {
          if (typeof value === 'object') {
            return { id: value.id };
          }
          return value;
        }),
      };
    },
    buildPayload(payload) {
      const { regras, criaHierarquia } = payload;
      return {
        id: this.alcada ? this.alcada.id : undefined,
        regras: regras.map((regra, index) => ({
          ...regra,
          $$id: undefined,
          sequencia: index + 1,
          criterios: regra.criterios.map(this.buildCriterios),
        })),
        criaHierarquia,
      };
    },
    onClickSave(payload) {
      this.loadingSave = true;
      this.saveFunction(payload)
        .then(() => {
          this.$toast.success('Alçadas atualizadas');
          this.getAlcadas();
        })
        .finally(() => {
          this.loadingSave = false;
        });
    },
    saveFunction(payload) {
      if (this.alcada) {
        return AlcadasRequisicaoService.update(this.alcada.id, this.buildPayload(payload));
      }
      return AlcadasRequisicaoService.create(this.buildPayload(payload));
    },
    onClickRemove() {
      this.$refs.confirm.open('Você deseja remover essa configuração?',
        'Esta operação não pode ser desfeita',
        { width: 400 })
        .then((confirm) => {
          if (!confirm) {
            return;
          }
          this.removeAlcadas();
        });
    },
    removeAlcadas() {
      this.loadingRemove = true;
      AlcadasRequisicaoService.remove(this.alcada.id)
        .then(() => {
          this.$toast.success('Alçadas removidas');
          this.getAlcadas();
        })
        .finally(() => {
          this.loadingRemove = false;
        });
    },
    buildOpcoesCriterios(cadastrosAdicionaisDisponiveis, opcoesCadastrosAdicionais) {
      const opcoesCriterios = CriteriosAprovacaoSolicitacao.values.filter(
        v => v.key !== CriteriosAprovacaoSolicitacao.keys.CADASTRO_ADICIONAL.key,
      );
      const opcoesDisponiveis = opcoesCadastrosAdicionais
        .filter(o => cadastrosAdicionaisDisponiveis.includes(o.key))
        .map(opcao => mapCadastroAdicionalAlcadaByErp(this.erpCamposIntegracao, opcao));

      const opcoes = [...opcoesCriterios, ...opcoesDisponiveis];
      this.criteriosEnum = opcoes;
      this.opcoesCriterios = opcoes;
    },
    getCamposErp() {
      if (!this.erpCamposIntegracao) {
        this.buildOpcoesCriterios([], []);
        return Promise.resolve({});
      }
      this.loading = true;
      return ErpCampoService.getCamposGeraisRequisicaoPorErp(this.erpCamposIntegracao)
        .then(({ data }) => {
          this.buildOpcoesCriterios(data, alcadasCadastrosAdicionaisConstants);
        }).finally(() => {
          this.loading = false;
        });
    },
  },
  created() {
    this.getCamposErp()
      .then(() => {
        this.getAlcadas();
      });
  },
  components: {
    pxAlcadasList: AlcadasList,
  },
};
</script>
